import { Breadcrumb, Button, Form, Input, InputNumber } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { ServerGatewayInfo } from '@/api/type-server.ts'
import LeftArrow from '@/assets/icons/left-arrow.svg?react'
import { Divider, message, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown.tsx'
import { Textarea } from '@/components/base/Textarea.tsx'
import { isImageLaunchUnavailable } from '@/consts/image'
import { GhostType, MachineType, PaymentType } from '@/consts/machine.ts'
import { BILLING_METHODS, DURATION } from '@/consts/order'
import { getDeployListPath, useUnifiedContext } from '@/contexts/UnifiedContext'
import { Deployment } from '@/models/deployment.ts'
import { Image } from '@/models/image.ts'
import { Machine } from '@/models/machine.ts'
import { Snapshot } from '@/models/snapshot.ts'
import { CreationViewSwitch } from '@/pages/create-instance/components/CreationViewSwitch'
import { ImageKeywordSearch } from '@/pages/create-instance/components/ImageKeywordSearch'
import { ImageMachineSelector } from '@/pages/create-instance/components/ImageMachineSelector'
import { MachineSelector } from '@/pages/create-instance/components/machine-selector.tsx'
import { useSelectionScope } from '@/pages/create-instance/hooks/useSelectionScope'
import { SliderInput } from '@/pages/instance-list/components/SliderInput.tsx'
import {
  EnhancedSnapshot,
  useTeamSnapshots,
} from '@/pages/teams/create-instance/hooks/useTeamSnapshots'

import { ScalingPolicyFields } from '../components/ScalingPolicyFields'
import {
  getScalingPolicyPayload,
  getScalingPolicyValues,
  ScalingPolicyValues,
} from '../scalingPolicy'
import { getDeploymentEditChanges, updateDeploymentPreservingConfig } from '../updateDeployment'
import { buildDeploymentTarget } from '../utils/deploymentTarget'

type FormValuesType = ScalingPolicyValues & {
  name: string
  description: string
  machine: Machine
  fromImageId: number
  fromSnapId: number
  port: number
  cmd: string
  ttlSeconds: number
}

export function DeploymentCreateSubPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const editingDeployment = location.state?.editingDeployment as Deployment
  const [form] = Form.useForm<FormValuesType>()
  const [loading, setLoading] = useState(false)
  const [gatewayLoading, setGatewayLoading] = useState(false)
  const [selectionView, setSelectionView] = useState<'instance' | 'image'>('instance')
  const [imageKeywords, setImageKeywords] = useState<string[]>([])
  const [keepExistingTarget, setKeepExistingTarget] = useState(Boolean(editingDeployment))
  const scope = useSelectionScope()
  const initialScope = useRef(scope)
  const ctx = useUnifiedContext()
  const deployListPath = getDeployListPath(ctx)

  const [type, setType] = useState(() => {
    if (editingDeployment) {
      return editingDeployment.runtimeEnv.hardwareEnv.resource.machineCategory
    }
    return MachineType.GPU
  })
  const period = PaymentType.Hourly
  const [count] = useState(1)
  const [checkedImage, setCheckedImage] = useState<Image>()
  const [checkedSnap, setCheckedSnap] = useState<EnhancedSnapshot>()
  const [isRequiredMachine, setIsRequiredMachine] = useState(false)
  const [gatewayList, setGatewayList] = useState<ServerGatewayInfo[]>([])
  const [machine, setMachine] = useState<Machine>()

  // Load team snapshots when in team context
  const { snapshots: teamSnapshots } = useTeamSnapshots(machine, count)

  const [selectedGhostType] = useState<GhostType>(() => {
    if (editingDeployment) {
      return editingDeployment.runtimeEnv.systemEnv.fromImageId !== 0
        ? GhostType.Image
        : GhostType.Snapshot
    }
    return GhostType.Image
  })

  const { t } = useTranslation('autoDeploy')

  const handleSetCheckedImage = (image?: Image) => {
    setCheckedImage(image)
    if (image) setCheckedSnap(undefined)
    form.setFieldsValue({ machine: machine })
    form.setFieldsValue({ fromImageId: image?.id })
    form.setFieldsValue({ fromSnapId: 0 })
  }

  const handleSetCheckedSnap = (snap?: Snapshot) => {
    setCheckedSnap(snap)
    if (snap) setCheckedImage(undefined)
    form.setFieldsValue({ machine: machine })
    form.setFieldsValue({ fromImageId: 0 })
    form.setFieldsValue({ fromSnapId: snap?.id })
  }

  useEffect(() => {
    if (scope !== initialScope.current) navigate(deployListPath, { replace: true })
  }, [scope, deployListPath, navigate])

  useEffect(() => {
    let active = true
    setGatewayList([])
    if (!machine?.domains?.length) {
      setGatewayLoading(false)
      return
    }
    setGatewayLoading(true)
    API.getGatewayList({ domain: machine.domains[0], public: true, page: 1, per_page: 5 })
      .then((res) => {
        if (active && res.code === 0) setGatewayList(res.gateways)
      })
      .finally(() => {
        if (active) setGatewayLoading(false)
      })
    return () => {
      active = false
    }
  }, [machine?.domains, scope])

  // Effect for setting basic form values when editing
  useEffect(() => {
    if (editingDeployment) {
      // Set basic form values immediately
      form.setFieldsValue({
        name: editingDeployment.name,
        description: editingDeployment.description,
        port: editingDeployment.gateway.port,
        cmd: editingDeployment.runtimeEnv.systemEnv.cmd,
        ttlSeconds: editingDeployment.ttlSeconds / 60, // Convert seconds to minutes
        ...getScalingPolicyValues(editingDeployment),
      })
    }
  }, [editingDeployment, form])

  async function handleSubmit(fieldsValue: FormValuesType) {
    if (scope !== initialScope.current) return
    const target = buildDeploymentTarget({
      saved: editingDeployment,
      keepExisting: keepExistingTarget,
      machine,
      image: checkedImage,
      snapshot: checkedSnap,
      count,
    })
    if (
      !target ||
      (!keepExistingTarget && checkedImage && isImageLaunchUnavailable(checkedImage))
    ) {
      setIsRequiredMachine(true)
      document.getElementById('fromImageId')?.scrollIntoView({ behavior: 'smooth' })
      return
    }
    const gateway = keepExistingTarget ? editingDeployment?.gateway : gatewayList[0]
    if (!gateway || (!keepExistingTarget && gatewayLoading)) {
      message.error(t('createSubPage.form.noGateway'))
      return
    }
    setLoading(true)
    try {
      const policy = getScalingPolicyPayload(
        { ...form.getFieldsValue(true), ...fieldsValue },
        editingDeployment,
      )
      if (editingDeployment) {
        await updateDeploymentPreservingConfig(
          API,
          editingDeployment.resID,
          getDeploymentEditChanges(editingDeployment, {
            name: fieldsValue.name,
            description: fieldsValue.description ?? '',
            port: fieldsValue.port,
            gtw_res_id: gateway.resID,
            ttl_seconds: fieldsValue.ttlSeconds * 60,
            ...policy,
            systemEnv: {
              cmd: fieldsValue.cmd ?? '',
              fromImageId: target.systemEnv.fromImageId,
              fromSnapshotId: target.systemEnv.fromSnapshotId,
            },
            ...(keepExistingTarget ? {} : { hardwareResource: target.resource }),
          }),
        )
      } else {
        const res = await API.createDeployment({
          port: fieldsValue.port,
          domain: target.resource.agentDomains[0] ?? 0,
          system_env: JSON.stringify({
            ...target.systemEnv,
            cmd: fieldsValue.cmd ?? '',
            extraPorts: [fieldsValue.port],
          }),
          name: fieldsValue.name,
          description: fieldsValue.description ?? '',
          gtw_res_id: gateway.resID,
          hardware_resource: JSON.stringify(target.resource),
          ttl_seconds: fieldsValue.ttlSeconds * 60,
          ...policy,
        })
        if (res.code !== 0) throw new Error(res.msg || 'Deployment creation failed')
      }
      navigate(deployListPath)
    } catch (error) {
      console.error(error)
      message.error(t(editingDeployment ? 'common.updateFailed' : 'common.createFailed'))
    } finally {
      setLoading(false)
    }
  }

  const changeView = (view: 'instance' | 'image') => {
    if (view === selectionView) return
    setMachine(undefined)
    setCheckedImage(undefined)
    setCheckedSnap(undefined)
    setSelectionView(view)
  }
  const viewSwitch = (
    <div className="shrink-0">
      <div className="t-sb text-secondary mb-2">
        {t('create.imageView.view', { ns: 'translation' })}
      </div>
      <CreationViewSwitch value={selectionView} onChange={changeView} withIcons={false} />
    </div>
  )

  return (
    <>
      <div className="card rounded-2xl bg-panel-white">
        <div className="flex items-center space-x-4 mb-4">
          <Link to={deployListPath}>
            <div className="w-8 h-8 icon-btn">
              <LeftArrow />
            </div>
          </Link>
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to={deployListPath} className="h6 text-secondary !hover:text-red-600">
                    <Trans i18nKey="title" ns="autoDeploy">
                      Auto Deploy
                    </Trans>
                  </Link>
                ),
              },
              {
                title: (
                  <span className="h6 text-primary">
                    {editingDeployment ? (
                      <Trans i18nKey="createSubPage.editTitle" ns="autoDeploy">
                        Edit Deploy
                      </Trans>
                    ) : (
                      <Trans i18nKey="createSubPage.title" ns="autoDeploy">
                        New Deploy
                      </Trans>
                    )}
                  </span>
                ),
              },
            ]}
            separator={<span className="h6 text-primary">/</span>}
          />
        </div>

        <Divider />

        <Form
          form={form}
          onFinish={handleSubmit}
          validateTrigger={['onChange', 'onBlur']}
          layout="vertical"
          requiredMark={false}
          initialValues={getScalingPolicyValues()}
          name="create-scaling-spec-form"
          scrollToFirstError={true}
        >
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.form.name" ns="autoDeploy">
                  Deploy Name
                </Trans>
              </div>
            }
            id="name"
            name="name"
            className="mb-8"
            rules={[{ required: true, message: t('createSubPage.form.nameRequired') }]}
          >
            <Input
              className="h-11 px-4 py-3 t-sb w-full max-w-[576px] border-neutral-200"
              placeholder={t('createSubPage.form.namePlaceholder')}
            />
          </Form.Item>
          <Form.Item
            label={
              <div className="t-sb">
                <Trans i18nKey="createSubPage.form.description" ns="autoDeploy">
                  Deploy Description
                </Trans>
              </div>
            }
            id="description"
            name="description"
            className="mb-8 w-[576px] max-w-full"
          >
            <Input.TextArea
              className="!h-[88px] px-4 py-3 t-sb w-full border-neutral-200"
              placeholder={t('createSubPage.form.descriptionPlaceholder')}
            />
          </Form.Item>

          <div className="mb-8 py-2">
            <span className="h6 pl-2 border-l-4 border-secondary">
              <Trans i18nKey="createSubPage.form.deployTargetTitle" ns="autoDeploy">
                Deploy Target
              </Trans>
            </span>
          </div>

          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.form.accessMethod" ns="autoDeploy">
                  Access Method
                </Trans>
              </div>
            }
            id="accessMethod"
            name="accessMethod"
            className="mb-8 t-sb w-[576px] max-w-full"
          >
            <Dropdown
              options={[
                {
                  label: t('createSubPage.form.accessMethodPublic'),
                  value: 'public',
                  className: 't-sb',
                },
                {
                  label: t('createSubPage.form.accessMethodPrivate'),
                  value: 'private',
                  className: 't-sb',
                  disabled: true,
                },
              ]}
            />
          </Form.Item>

          <div id="fromImageId" className="mb-4 t-sb text-with-star">
            <Trans i18nKey="createSubPage.form.instanceImage" ns="autoDeploy">
              Instance & Image
            </Trans>
          </div>
          {isRequiredMachine &&
            !keepExistingTarget &&
            (!machine || (!checkedImage && !checkedSnap)) && (
              <div role="alert" className="mb-4 text-error t-sb">
                {t('createSubPage.form.selectTarget')}
              </div>
            )}

          <Form.Item name="machine" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="fromImageId" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="fromSnapId" hidden>
            <Input />
          </Form.Item>

          {keepExistingTarget && editingDeployment ? (
            <div
              className="mb-8 rounded-lg border border-neutral-200 p-5"
              role="status"
              data-ai-component="saved-deployment-target"
            >
              <div className="t-sb mb-2">{t('createSubPage.form.savedTarget')}</div>
              <p className="t-sm text-secondary mb-2">
                {t('createSubPage.form.savedTargetSource', {
                  source: t(
                    editingDeployment.runtimeEnv.systemEnv.fromImageId
                      ? 'create.bootSourceType.image'
                      : 'create.typeLabelSnapshot',
                    { ns: 'translation' },
                  ),
                  id:
                    editingDeployment.runtimeEnv.systemEnv.fromImageId ||
                    editingDeployment.runtimeEnv.systemEnv.fromSnapshotId,
                })}
              </p>
              <p className="t-sm text-secondary mb-2">
                {editingDeployment.runtimeEnv.hardwareEnv.resource.gpuName ||
                  editingDeployment.runtimeEnv.hardwareEnv.resource.cpuName}
              </p>
              <p className="t-sm text-tertiary mb-4">{t('createSubPage.form.savedTargetHint')}</p>
              <Button
                htmlType="button"
                onClick={() => {
                  setKeepExistingTarget(false)
                  setMachine(undefined)
                  setCheckedImage(undefined)
                  setCheckedSnap(undefined)
                }}
              >
                {t('createSubPage.form.changeTarget')}
              </Button>
            </div>
          ) : (
            <>
              {editingDeployment && (
                <Button
                  htmlType="button"
                  className="mb-4"
                  onClick={() => {
                    setKeepExistingTarget(true)
                    setMachine(undefined)
                    setCheckedImage(undefined)
                    setCheckedSnap(undefined)
                    setIsRequiredMachine(false)
                  }}
                >
                  {t('createSubPage.form.keepTarget')}
                </Button>
              )}
              {selectionView === 'image' ? (
                <ImageMachineSelector
                  key={scope}
                  type={type}
                  onTypeChange={setType}
                  machine={machine}
                  onMachineChange={setMachine}
                  image={checkedImage}
                  onImageChange={handleSetCheckedImage}
                  count={count}
                  keywords={imageKeywords}
                  billingMethod={BILLING_METHODS.PAY_AS_YOU_GO}
                  duration={DURATION.DAILY}
                  supportsExtraResources={false}
                  toolbarStart={viewSwitch}
                  toolbarEnd={
                    <div className="ml-auto">
                      <ImageKeywordSearch
                        value={imageKeywords}
                        onChange={(keywords) => {
                          setImageKeywords(keywords)
                          setMachine(undefined)
                          setCheckedImage(undefined)
                        }}
                      />
                    </div>
                  }
                />
              ) : (
                <MachineSelector<EnhancedSnapshot>
                  key={scope}
                  paginateImagesOnServer
                  imageFiltersInToolbar
                  toolbarStart={viewSwitch}
                  type={type}
                  period={period}
                  onTypeChange={setType}
                  onChange={setMachine}
                  count={count}
                  checkedImage={checkedImage}
                  setCheckedImage={handleSetCheckedImage}
                  checkedSnap={checkedSnap}
                  setCheckedSnap={handleSetCheckedSnap}
                  selectedMachine={machine}
                  selectedGhostType={selectedGhostType}
                  workloadTypes={['all']}
                  teamSnapshots={ctx.mode === 'team' ? teamSnapshots : undefined}
                  enforceBootSourceResourceLimit
                  supportsExtraResourcesForBootSource={false}
                  imageEmptyMessageVariant={ctx.mode === 'team' ? 'team' : 'default'}
                  billingMethod={BILLING_METHODS.PAY_AS_YOU_GO}
                  duration={DURATION.DAILY}
                />
              )}
            </>
          )}

          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.form.port" ns="autoDeploy">
                  Port (HTTP/HTTPS)
                </Trans>
              </div>
            }
            name="port"
            rules={[{ required: true, message: t('createSubPage.form.portRequired') }]}
          >
            <InputNumber
              className="h-11 t-sb w-full max-w-[576px] py-2 border-neutral-200"
              placeholder={t('createSubPage.form.portPlaceholder')}
              min={0}
              max={65535}
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb">
                <Trans i18nKey="createSubPage.form.cmd" ns="autoDeploy">
                  Start Command
                </Trans>
              </div>
            }
            name="cmd"
          >
            <Textarea
              className="h-[88px] min-h-[88px] border border-neutral-200 px-4 py-3 w-full max-w-[576px]"
              placeholder={t('createSubPage.form.cmdPlaceholder')}
            />
          </Form.Item>

          <div className="mb-8 py-2">
            <span className="h6 pl-2 border-l-4 border-secondary">{t('scalingPolicy.title')}</span>
          </div>
          <div className="mb-8">
            <ScalingPolicyFields disabled={loading} savedMaximum={editingDeployment?.maxReplicas} />
          </div>

          <div className="mb-8">
            <div className="t-sb mb-2">
              <Trans i18nKey="createSubPage.form.ttlSeconds" ns="autoDeploy">
                Instance Idle Retention Period
              </Trans>
            </div>
            <Form.Item
              name="ttlSeconds"
              initialValue={5}
              rules={[{ required: true, message: t('createSubPage.form.ttlSecondsRequired') }]}
              className="mb-0 w-full max-w-[576px]"
            >
              <SliderInput
                min={3}
                max={90}
                unit={t('createSubPage.form.ttlSecondsUnit')}
                step={1}
                onChange={() => {}}
                showReset={false}
              />
            </Form.Item>
          </div>

          <Form.Item>
            <div className="pt-4 flex items-end justify-end gap-4">
              <Button
                size="large"
                className="h-11 px-4 py-3 t-sb"
                onClick={() => {
                  navigate(deployListPath)
                }}
              >
                <Trans i18nKey="createSubPage.form.cancelBtn" ns="autoDeploy">
                  Cancel
                </Trans>
              </Button>
              <Button
                type="primary"
                className="h-11 px-4 py-3 t-sb"
                size="large"
                loading={loading}
                htmlType={'submit'}
                disabled={scope !== initialScope.current || (!keepExistingTarget && gatewayLoading)}
              >
                <Trans i18nKey="createSubPage.form.confirmBtn" ns="autoDeploy">
                  Confirm
                </Trans>
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </>
  )
}
